import moment from "moment";

import DSBExceptionDetail from "issara-sdk/apis/DSBExceptionDetail_apps_QUE";
import DSBExceptionList from "issara-sdk/apis/DSBExceptionList_apps_QUE";

import {
  ActionHandler,
  BaseSeqState,
  BLC_STATUS,
  GenerateActionTypes,
  generatePrefixedActions,
  PickMainState,
  SetErrorMessage,
} from "react-lib/apps/HISV3/common/CommonInterface";

import { formatHolidayItem } from "react-lib/apps/Scheduling/SchedulingInterface";

import { adToBeWithSetFormat } from "react-lib/utils/dateUtils";

/** ============== Type-Defining Constants =============  */

export const ACTIONS = {
  COPY_YEAR: "COPY_YEAR",
  DELETE: "DELETE",
  SAVE: "SAVE",
  SEARCH: "SEARCH",
} as const;

export const SEQUENCE_NAME = "HolidaySetting" as const;

/** =============== Types and Interfaces ===============  */
export type State = Partial<{
  HolidaySettingSequence: Partial<{
    sequenceIndex: "Action" | "START" | null;
    holiday: HolidayDataType;
  }> | null;
}>;

export type PickedState = PickMainState<"buttonLoadCheck" | "errorMessage">;

type HolidayDataType = {
  id: number | null;
  division: number | null;
  follow_parent: boolean;
  items: HolidayItemDataType[];
};

type HolidayItemDataType = HolidayInfoType & { day?: number; month?: number };

export type HolidayInfoType = { dates: string[]; detail: string; index?: number; remark: string };

export type Event =
  | { message: "GetMasterData"; params: Record<string, unknown> }
  | { message: "RunSequence"; params: Record<string, unknown> };

export type Data = { division?: number };

export type SeqState = BaseSeqState<SequenceName>;

export type Actions = typeof ACTIONS;

type SequenceName = typeof SEQUENCE_NAME;

type ActionPayloadMap = {
  [ACTIONS.COPY_YEAR]: { year: number; onError: () => void; onSuccess: () => void };
  [ACTIONS.DELETE]: { data: HolidayInfoType; onSuccess: () => void };
  [ACTIONS.SAVE]: { data: HolidayInfoType; onSuccess: () => void };
};

type ActionTypes = GenerateActionTypes<
  Actions,
  ActionPayloadMap,
  SequenceName,
  PickedState & State
>;

type ActionType = ActionTypes["ActionType"];

export type RunSequence = ActionTypes["RunSequence"];

export type SetProp = ActionTypes["SetProp"];

export type OnEvent = ActionTypes["OnEvent"];

type Params<A extends ActionType["action"]> = ActionTypes["Params"][A];

export type Handler<P = unknown, R = void> = ActionHandler<PickedState & State, Event, Data, P, R>;

/** ===================== Constants ====================  */
export const StateInitial: State = {
  HolidaySettingSequence: null,
};

export const DataInitial: Data = {};

export const BTN_ACTS = generatePrefixedActions(ACTIONS, SEQUENCE_NAME);

/** ===================== Functions ====================  */
export const Start: Handler<SeqState> = async (controller, params) => {
  const state = controller.getState();

  controller.setState(
    { HolidaySettingSequence: { ...state.HolidaySettingSequence, sequenceIndex: "Action" } },
    () => {
      Action(controller, { ...params, action: ACTIONS.SEARCH });
    }
  );
};

/** ====================================================  */
/**                        Action                         */
/** ====================================================  */
export const Action: Handler<ActionType> = async (controller, params) => {
  const actionHandlers: Partial<{ [K in ActionType["action"]]: Handler<Params<K>> }> = {
    [ACTIONS.COPY_YEAR]: HandleCopyToNextYear,
    [ACTIONS.DELETE]: HandleDelete,
    [ACTIONS.SAVE]: HandleSave,
    [ACTIONS.SEARCH]: HandleSearch,
  };

  const { action } = params;

  const handler = actionHandlers[action] as Handler<Params<typeof action>> | undefined;

  if (handler) {
    handler(controller, params);
  } else {
    console.error(`No handler found for action: ${action}`);
  }
};

/** ====================================================  */
/**                        Handler                        */
/** ====================================================  */
const HandleSearch: Handler<unknown, Promise<void>> = async (controller) => {
  const state = controller.getState();

  const [result] = await DSBExceptionList.list({
    apiToken: controller.apiToken,
    params: { division_id: controller.data.division },
  });

  const items: Record<string, any>[] = result?.items ?? [];

  if (items.length > 0) {
    const holidayItem = items[0] as HolidayDataType;

    holidayItem.items = holidayItem.items
      .map((element) => formatHolidayItem(element) as HolidayItemDataType)
      .map((item, index) => {
        const { day, month, ...rest } = item;

        console.log(day, month);

        return { ...rest, index };
      });

    controller.setState({
      HolidaySettingSequence: {
        ...state.HolidaySettingSequence,
        holiday: holidayItem as HolidayDataType,
      },
    });

    return;
  }

  controller.setState({
    HolidaySettingSequence: {
      ...state.HolidaySettingSequence,
      holiday: {
        id: null,
        division: controller.data.division ?? null,
        follow_parent: true,
        items: [],
      },
    },
  });
};

const HandleSave: Handler<Params<Actions["SAVE"]>> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SAVE]: BLC_STATUS.LOADING },
  });

  const { data } = params;
  const holiday = state.HolidaySettingSequence?.holiday;
  const holidayItems = holiday?.items ?? [];

  const items = formatHolidayItems(data);

  let merged: typeof holidayItems;

  // กรณีมี index: update ที่ตำแหน่งนั้นและลบที่วันชน
  if (typeof data.index === "number") {
    const updateItem = items[0];
    const oldItem = holidayItems[data.index];

    merged = holidayItems
      .filter(
        (item, index) =>
          index === data.index || !item.dates.some((date) => updateItem.dates.includes(date))
      )
      .map((item) => (item === oldItem ? updateItem : item));
  }
  // กรณีไม่มี index: เพิ่ม items ใหม่ทั้งหมดและลบที่วันชน
  else {
    merged = [...holidayItems];

    for (const updateItem of items) {
      merged = merged.filter((item) => !item.dates.some((date) => updateItem.dates.includes(date)));

      merged.push(updateItem);
    }
  }

  const [, error] = await CreateUpdateDSBException(controller, { items: merged });

  if (error) {
    SetErrorMessage(controller, { ...params, error });

    return;
  }

  await HandleSearch(controller);

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SAVE]: null },
  });

  params.onSuccess();
};

const HandleDelete: Handler<Params<Actions["DELETE"]>> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.DELETE]: BLC_STATUS.LOADING },
  });

  const { data } = params;
  const holiday = state.HolidaySettingSequence?.holiday;
  const holidayItems = holiday?.items ?? [];

  const itemsToDelete = formatHolidayItems(data);

  const remainingItems = holidayItems.filter(
    (item) =>
      !itemsToDelete.some((deleteItem) =>
        deleteItem.dates.every((date) => item.dates.includes(date))
      )
  );

  const [, error] = await CreateUpdateDSBException(controller, { items: remainingItems });

  if (error) {
    SetErrorMessage(controller, { ...params, error });

    return;
  }

  await HandleSearch(controller);

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.DELETE]: null },
  });

  params.onSuccess();
};

const HandleCopyToNextYear: Handler<Params<Actions["COPY_YEAR"]>> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.COPY_YEAR]: BLC_STATUS.LOADING },
  });

  const { year } = params;
  const holiday = state.HolidaySettingSequence?.holiday;
  const currentYearItems = holiday?.items ?? [];

  const updateHolidays = getCopiedHolidaysForNextYear(currentYearItems, year);

  const mergedItems = [...currentYearItems, ...updateHolidays];

  const [, error] = await CreateUpdateDSBException(controller, { items: mergedItems });

  if (error) {
    SetErrorMessage(controller, { ...params, error });

    params.onError();

    return;
  }

  await HandleSearch(controller);

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.COPY_YEAR]: BLC_STATUS.SUCCESS },
  });

  params.onSuccess();
};

// APIS
const CreateUpdateDSBException: Handler<{ items: any[] }, Promise<[unknown, unknown]>> = async (
  controller,
  params
) => {
  const state = controller.getState();

  const holiday = state.HolidaySettingSequence?.holiday;
  const api = holiday?.id ? DSBExceptionDetail.update : DSBExceptionList.create;

  return api({
    apiToken: controller.apiToken,
    data: {
      ...holiday,
      items: params.items,
    } as any,
    pk: holiday?.id,
  }) as Promise<[unknown, unknown]>;
};

/** ====================================================  */
/**                         Utils                         */
/** ====================================================  */
const formatHolidayItems = (data: HolidayInfoType): HolidayItemDataType[] =>
  data.dates.map((date) => {
    const [day, month, year] = date.split("-");

    return {
      date: [`${day}/${month}/${year}`],
      dates: [`${Number(year) - 543}-${month}-${day}`],
      detail: data.detail,
      remark: data.remark,
    };
  });

const getCopiedHolidaysForNextYear = (
  items: HolidayItemDataType[],
  currentYear: number
): HolidayInfoType[] => {
  // กรองวันหยุดของปีปัจจุบัน
  const currentYearHolidays = items.filter((item) => moment(item.dates[0]).year() === currentYear);

  // สร้างวันหยุดสำหรับปีหน้า
  const nextYearHolidays = currentYearHolidays.map((item) => {
    const nextYearDate = moment(item.dates[0]).add(1, "year");
    const date = nextYearDate.format("YYYY-MM-DD");

    return {
      date: [adToBeWithSetFormat(date, "YYYY-MM-DD", "DD-MM-YYYY")],
      dates: [nextYearDate.format("YYYY-MM-DD")],
      detail: item.detail,
      remark: item.remark,
    };
  });

  // ตรวจสอบวันหยุดที่มีอยู่แล้วในปีหน้า
  const existingNextYearItems = items.filter(
    (item) => moment(item.dates[0]).year() === currentYear + 1
  );

  // กรองเฉพาะวันหยุดที่ไม่ซ้ำ (เช็คทั้ง dates และ detail)
  return nextYearHolidays.filter(
    (item) =>
      !existingNextYearItems.some(
        (existingItem) =>
          existingItem.dates[0] === item.dates[0] && existingItem.detail === item.detail
      )
  );
};
